import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, model, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
// import { UserService } from 'src/app/services/user.service';
import { IconService } from 'src/app/ui/icon.service';
import { NavItem, ThemeItem } from '../../../models/navigation.model';
import { Themes } from '../../../models/navigation.model';
import { useMcxTheme, useTealTheme } from 'src/themes/theme-utilities';
import { ThemeService } from 'src/app/services/theme.service';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { KpiReportService } from 'src/app/api/kpiReport.service';
import { UserwayService } from 'src/app/services/userway.service';
import { McSettingsService } from 'src/app/api/mc-settings.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { StaffV2Service } from 'src/app/api/staffV2.service';
import { ConfigService } from 'src/app/config.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NavigationComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  navItems: WritableSignal<NavItem[]> = signal([]);
  themeItem?: NavItem;
  signInString: string = 'SIGN IN';
  username: string = '';

  showAccessibilityDialog = model(false)
  accessibilityEnabled = signal(false)

  // Cache for navigation items to avoid rebuilding
  private navItemsCache: NavItem[] | null = null;


  constructor(public icons: IconService,
    public auth: LocalAuthService,
    private changeDetector: ChangeDetectorRef,
    private userwayService: UserwayService,
    private mcSettingsService: McSettingsService,
    private staffService: StaffV2Service,
    private themeService: ThemeService,
    private configService: ConfigService
  ) {
    this.navItems.set(this.getNavItems());
    // this.accountMenuItems = this.getAccountMenuItems();
    this.themeItem = this.getThemeItem();

  }

  ngOnInit(): void {
    this.getUsername();
    useMcxTheme();
    this.getAccessibilitySettings();

    // Clear navigation cache when user changes to ensure role-based navigation updates
    this.auth.currentUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.navItemsCache = null;
      this.navItems.set(this.getNavItems());
    });
  }

  loggedInUser = toSignal(this.auth.currentUser$)

  getUsername() {
    this.auth.currentUser$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        if (user?.name) {
          this.setUsername(user.name.split(' ')[0]);
          this.changeDetector.detectChanges();
        } else {
          this.setUsername(this.signInString);
        }
      })
  }

  getAccessibilitySettings() {
    this.mcSettingsService.getUserwayEnabled().subscribe(enabled => {
      this.accessibilityEnabled.set(enabled)
    })
  }

  setUsername(name: string) {
    this.username = name;
  }

  hasStaffId(staffIds: number[]): boolean {
    const user = this.auth.currentUser;
    return user ? staffIds.includes(user.newstaffId) : false;
  }

  getToolsMenuItems(): MenuItem[] {
    const toolsMenuItems: MenuItem[] = [];

    // Remove Old Calls - MC - restricted to staff ID 83 or IT role
    if (this.auth.hasRole('it') || this.hasStaffId([83, 418])) {
      toolsMenuItems.push({
        label: 'REMOVE OLD CALLS',
        routerLink: 'tools/remove-old-calls'
      });
    }

    // Merge Records and Delete Record - restricted to admin role or specific emails
    if (this.auth.hasRole('admin') || this.auth.hasRole('it') || this.hasEmail(['leah.brotzman@prayingpelicanmissions.org', 'jim.noreen@prayingpelicanmissions.org'])) {
      toolsMenuItems.push(
        {
          label: 'DELETE RECORD',
          routerLink: 'tools/delete-record'
        },
        {
          label: 'MERGE RECORDS',
          routerLink: 'tools/merge-records'
        }
      );
    }

    if (this.auth.hasRole('it')) {
      toolsMenuItems.push(
        {
          label: 'STYLE GUIDE',
          routerLink: 'style-guide'
        }
      )
    }

    if (this.auth.hasRole('marketing') || this.auth.hasRole('it')) {
      toolsMenuItems.push({
        label: 'PHOTOBOOTH',
        routerLink: 'tools/photobooth'
      })
    }


    // General tools available to all
    toolsMenuItems.push(
      {
        label: 'GENERIC INVOICE',
        routerLink: 'tools/generic-invoice'
      },
      {
        label: 'REPORTS',
        routerLink: 'tools/reports'
      },
      {
        label: 'MERGE REQUEST',
        url: 'https://docs.google.com/a/prayingpelicanmissions.org/spreadsheet/viewform?usp=drive_web&formkey=dEFwQVQ0alpONGhMTG1GMFRFdHdFS1E6MQ',
      }
    );

    return toolsMenuItems;
  }

  hasEmail(emails: string[]): boolean {
    const user = this.auth.currentUser;
    return user ? emails.includes(user.email) : false;
  }

  getNavItems(): NavItem[] {
    // Return cached items if available
    if (this.navItemsCache) {
      return this.navItemsCache;
    }

    const navItems: NavItem[] = [];

    // RECRUITING section - restricted to sales or admin roles
    if (this.auth.hasAnyRole(['sales', 'admin'])) {
      const recruitingMenuItems: MenuItem[] = [
        {
          label: 'DASHBOARD',
          routerLink: 'recruiting/dashboard',
        },
        { label: 'WIKI', url: 'https://sites.google.com/prayingpelicanmissions.org/recruiting/recruiting-resources' },
        { label: 'LEAD SEARCH', routerLink: 'recruiting/lead-search' },
        { label: 'POTENTIAL TRIP SEARCH', routerLink: 'recruiting/pTrips' },
        { label: 'CREATE NEW LEAD', routerLink: "new-lead" },
        { label: 'TEAMS REGISTERED', routerLink: 'recruiting/teams-registered' },
        { label: 'RENEWALS', routerLink: 'recruiting/renewal-report' }
      ];

      // Web Leads - restricted to salesAdmin role
      if (this.auth.hasRole('salesAdmin')) {
        recruitingMenuItems.push({ label: 'WEB LEADS', routerLink: 'recruiting/web-leads' });
      }

      // Promo Codes - restricted to specific staff IDs or IT role
      if (this.auth.hasRole('it') || this.hasStaffId([106, 418])) {
        recruitingMenuItems.push({ label: 'PROMO CODES', routerLink: 'recruiting/promo-codes' });
      }

      navItems.push({
        label: 'RECRUITING',
        icon: this.icons.solid.faUserMagnifyingGlass,
        hasSubItems: true,
        menuItems: recruitingMenuItems,
        showOnlyForRoles: ['sales', 'admin']
      });
    }

    let tripsSubItems: MenuItem[] = [
      { label: 'LEADERSHIP HANDBOOK', url: 'https://leader-app.ppm.org/' },
      { label: 'PELICAN GUIDE', url: 'https://app.ppm.org/' }
    ]

    // Manage - restricted to ops role
    if (this.auth.hasAnyRole(['ops'])) {
      tripsSubItems.push({
        label: "MANAGE",
        routerLink: 'trips/manage'
      })
    }

    let incidentsSubItems: MenuItem[] = [
      { label: 'INCIDENT GUIDELINES', url: 'https://docs.google.com/document/d/18HJkw5TCbjJZOZtrNtF431ryeTM74dXQbSR-Galvq4E/edit?usp=sharing' },
      { label: 'REPORT AN INCIDENT', routerLink: 'incidents/pick-report-type' },
      { label: 'MISSING PERSON(S)', routerLink: 'incidents/incident-reports/missing-person/create' },
    ]

    if (this.auth.hasRole('crisis')) {
      incidentsSubItems.push({
        label: "INCIDENT MANAGEMENT",
        routerLink: 'incidents'
      })
    }
    else {
      incidentsSubItems.push({
        label: "MY SUBMITTED REPORTS",
        routerLink: 'incidents'
      })
    }

    const participantSubMenuItems: MenuItem[] = [
      { label: 'PARTICIPANTS BY DATE', routerLink: 'executive-reporting/participants-by-date' },
      { label: 'WEEKLY ACTUAL REPORT', routerLink: 'trips/weekly-actual-report' }
    ]


    const insightsSubMenuItems: MenuItem[] = [
      { label: 'MINISTRY HOURS', routerLink: 'trips/ministry-hours' },
      { label: 'INACTIVE TEAMS', routerLink: 'executive-reporting/inactive-teams' },
      { label: 'LOCAL STAFFING REPORT', routerLink: 'trips/local-staffing-report' }

    ]

    const surveySubItems: MenuItem[] = [
      { label: 'MY REQUESTED REPORTS', routerLink: 'forms/responses/my' },
    ]

    // GL/Participant Surveys - restricted to left role (using 'ft' as equivalent)
    if (this.auth.hasRole('ft')) {
      surveySubItems.push(
        { label: 'GL/PARTICIPANT SURVEYS', routerLink: 'trips/surveys' },
        { label: 'STAFF REPORTS AND PARTNER SURVEYS', routerLink: 'forms/reports' }
      );
    }

    if (this.auth.hasAnyRole(['forms/manage'])) {
      surveySubItems.push({ label: 'MANAGE FORMS', routerLink: 'forms/list' })
    }

    tripsSubItems.push(
      { label: 'TRIP DETAILS', routerLink: 'trips/details' },
    )

    // Staffing - restricted by settings
    if (this.auth.hasAnyRole(['staffingTool'])) {
      tripsSubItems.push({ label: 'STAFFING', routerLink: '/trip-staffing' });
    }

    tripsSubItems.push(
      { label: 'JOURNALS', routerLink: '/trip-leading/journals' },
      {
        label: 'INCIDENTS',
        hasSubItems: true,
        items: incidentsSubItems
      },
      { label: 'PARTICIPANTS', hasSubItems: true, items: participantSubMenuItems },
      { label: 'INSIGHTS', hasSubItems: true, items: insightsSubMenuItems },
      { label: 'SURVEYS/REPORTS', hasSubItems: true, items: surveySubItems },


      { label: 'STANDARD OPERATING PROCEDURES', url: `${this.configService.mcHost}/missioncontrol/upgrade/seasonal/docs/Basic%20Safety%20Standard%20Operating%20Procedures24.pdf` },
      { label: 'CHECK-IN', url: 'https://checkin.ppm.org/' }
    )

    navItems.push(
      {
        label: 'TRIPS',
        hasSubItems: true,
        icon: this.icons.solid.faPlane,
        menuItems: tripsSubItems
      },
    )

    const consultationSubItems: MenuItem[] = [
      { label: 'WIKI', url: 'https://sites.google.com/prayingpelicanmissions.org/recruiting/consulting-resources' },
      {
        label: 'DOCUMENTS',
        items: [
          {
            label: 'PPM\'S ROLE',
            url: 'https://docs.google.com/document/d/1KqfK3D2SCyPP7p7ChWe0Um9nRci6lx-qMLF95gAiLtA/edit?usp=sharing'
          },
          {
            label: 'PRACTICAL PARTNERSHIP',
            url: 'https://docs.google.com/document/d/10QY3ljMYGyDAHAkFM485rpx2ip_gyINDI7n6-CZwqoA/edit?usp=sharing'
          },
          {
            label: 'STATEMENT OF FAITH',
            url: 'https://www.ppm.org/about-us#statement-of-faith'
          }
        ]
      },
      {
        label: 'GUIDES',
        items: [
          {
            label: 'GROUP LEADER',
            url: 'https://drive.google.com/file/d/1aSU3nTiURvxClrO8AKdXQiVKEOvRpzVi/view'
          },
          {
            label: 'HOST PASTOR (EN)',
            url: 'https://drive.google.com/file/d/1L4NxvMorgTg7PuXvxIAYfoOqDYYzmUXe/view'
          },
          {
            label: 'HOST PASTOR (ES)',
            url: 'https://drive.google.com/file/d/11l3VX9ZPt9Tt3jG3bmHTi1dA1HAI7X0M/view'
          }
        ]
      },
      {
        label: 'CONSULTATION REPORT',
        routerLink: 'consulting/report'
      },
      {
        label: 'MESSAGES',
        routerLink: 'consulting/gl-mail'
      },
      {
        label: 'CALL REPORT',
        routerLink: 'executive-reporting/call-report'
      }
    ];

    // Notes Report - restricted to IT role or notesReport staff group (using 'it' for now)
    if (this.auth.hasAnyRole(['it', 'notesReport'])) {
      consultationSubItems.push({
        label: 'NOTES REPORT',
        routerLink: 'executive-reporting/customer-log'
      });
    }

    consultationSubItems.push(
      {
        label: 'PARTICIPANT REPORT',
        routerLink: 'consulting/participant-report'
      },
      {
        label: 'PARTNER CARE REPORT',
        routerLink: 'consulting/partner-care-report'
      },
      {
        label: 'TERMS EXCEPTIONS',
        url: 'https://docs.google.com/forms/d/e/1FAIpQLSfZOOhNFD8KE94YbKyrc9eLOXMEenKLkwHv83tcw-WBVWkqYw/viewform'
      }
    );

    // Team Shirt Orders - restricted to admin role
    if (this.auth.hasRole('admin')) {
      consultationSubItems.push({
        label: 'TEAM SHIRT ORDERS',
        routerLink: 'consulting/team-shirts'
      });
    }

    // Manage Trip Flags - restricted to riskyTeamManage, IT, or opsmgr roles
    if (this.auth.hasAnyRole(['riskyTeamManage', "it", "opsmgr"])) {
      consultationSubItems.push({
        label: 'MANAGE TRIP FLAGS',
        routerLink: 'consulting/manage-flags'
      })
    }

    navItems.push(
      {
        label: 'CONSULTATION',
        icon: this.icons.solid.faPeopleArrows,
        hasSubItems: true,
        menuItems: consultationSubItems
      },
    )

    navItems.push(

      {
        label: 'PPM365',
        icon: this.icons.solid.faEarthAmericas,
        hasSubItems: true,
        menuItems: [
          {
            label: 'UPDATES',
            routerLink: 'ppm365/updates'
          },
          {
            label: 'DONOR CARE REPORT',
            routerLink: 'ppm365/donor-care-report'
          }
        ]
      },
    )


    // Build DEPARTMENTS menu more efficiently
    const departmentsMenuItems: MenuItem[] = [];

    const hrStaffMenuItems: MenuItem[] = []

    // MC Active Staff - restricted to HR or IT roles
    if (this.auth.hasAnyRole(['hr', 'it'])) {
      hrStaffMenuItems.push({
        label: 'MC ACTIVE STAFF',
        routerLink: 'staff/list'
      })
    }

    hrStaffMenuItems.push({
      label: 'BAMBOO ACTIVE STAFF',
      routerLink: 'component2'
    })

    const hrMenuItems: MenuItem[] = [
      {
        label: 'EMPLOYEE DIRECTORY',
        url: 'https://prayingpelicanmissions.bamboohr.com/employees/directory.php'
      },
      {
        label: 'DOCUMENT LIBRARY',
        routerLink: 'document-library/hr'
      }
    ];

    // Supervisee KPIs - restricted to HR, IT, or super roles
    if (this.auth.hasAnyRole(['hr', 'it', 'super'])) {
      hrMenuItems.push({
        label: 'SUPERVISEE KPIS',
        routerLink: 'kpi/supervisee-kpi-report'
      })
    }

    // Year End Review - restricted to HR and IT roles
    // if (this.auth.hasAnyRole(['hr', 'reviewAdmin', 'it', 'head'])) {
    hrMenuItems.push({
      label: 'YEAR END REVIEW',
      routerLink: 'kpi/home'
    })
    // }

    hrMenuItems.push({
      label: 'SEASONAL STAFFING REPORT',
      routerLink: 'hr/seasonal-staffing'
    });

    // MC Active Staff - restricted to HR or IT roles
    if (this.auth.hasAnyRole(['hr', 'it'])) {
      hrMenuItems.push({
        label: 'MC ACTIVE STAFF',
        routerLink: 'staff/list'
      })
    }

    // hrMenuItems.push(
    //   {
    //     label: 'STAFF',
    //     hasSubItems: true,
    //     items: hrStaffMenuItems
    //   },
    //   {
    //     label: 'PTO REQUEST'
    //   },
    //   {
    //     label: 'ICHRA',
    //     items: [
    //       {
    //         label: 'ICHRA NOTICE',
    //         url: 'https://drive.google.com/file/d/1JkDb_1KTXGGKajV8L_zKrsc5P9mL39uz/view?usp=sharing'
    //       },
    //       {
    //         label: 'ATTESTATION FORM'
    //       }
    //     ]
    //   }
    // );

    // Applicants/Openings - restricted to HR role only
    if (this.auth.hasRole('hr')) {
      hrMenuItems.push({
        label: 'APPLICANTS/OPENINGS',
        items: [
          {
            label: 'APPLICANTS - BAMBOO HR',
            url: 'https://prayingpelicanmissions.bamboohr.com/hiring/candidates'
          },
          {
            label: 'JOB OPENINGS - BAMBOO HR',
            url: 'https://prayingpelicanmissions.bamboohr.com/hiring/jobs'
          }
        ]
      });
    }

    departmentsMenuItems.push(
      {
        label: 'HR',
        items: hrMenuItems
      }
    );

    const marketingMenuItems: MenuItem[] = [
      {
        label: 'MARKETING REQUESTS',
        url: 'https://docs.google.com/forms/d/e/1FAIpQLSf1kXyQ_TWLyVuYGuyIO175okpsvvf6k0wJ0i18cSQHruYvdA/viewform'
      },
      {
        label: 'SUBMIT A STORY',
        url: 'https://docs.google.com/forms/d/e/1FAIpQLScswB8_dgmuw2l_GQwW25BfeGKpIQR0_tW0dE8vhxXEqV8ZYQ/viewform'
      },
      {
        label: 'RESOURCES',
        url: 'https://drive.google.com/drive/folders/1TMlbo4-bhEKg-xzEe9mrqeB6N1Iv1oX9?usp=sharing'
      },
      {
        label: 'VIDEO LIBRARY',
        url: 'https://www.youtube.com/@PrayingPelicanMissions'
      }
    ];

    // Send SMS - restricted to marketing role
    if (this.auth.hasRole('marketing')) {
      marketingMenuItems.push({
        label: 'SEND SMS',
        routerLink: 'recruiting/text-message'
      });
    }

    departmentsMenuItems.push({
      label: 'MARKETING',
      items: marketingMenuItems
    });


    const techMenuItems: MenuItem[] = [
      {
        label: 'TICKETS',
        url: 'https://app.slack.com/client/T0CCGPQTY/C066LR0RQMR'
      }
    ];

    if (this.auth.hasAnyRole(['it'])) {

      techMenuItems.push({
        label: 'REVALIDATE MAIN SITE',
        routerLink: 'tech/revalidate-main-site'
      })

      techMenuItems.push({
        label: 'DELETE JOURNAL COMMENT',
        routerLink: 'tech/delete-journal-comment'
      })

      techMenuItems.push({
        label: 'REBUILD PAYMENT SCHEDULE',
        routerLink: 'tech/rebuild-payment-schedule'
      })

    }

    departmentsMenuItems.push(
      {
        label: 'TECH',
        items: techMenuItems
      }
    );

    const financeMenuItems: MenuItem[] = []

    // Expense Reports - restricted to finance, IT, ops, or staff ID 26
    if (this.auth.hasAnyRole(['finance', 'it', 'ops']) || this.hasStaffId([26])) {
      financeMenuItems.push({
        label: 'EXPENSE REPORTS',
        routerLink: 'finance/expense-reports'
      });
    }

    // Trip Liability - restricted to finance, IT, or specific staff IDs
    if (this.auth.hasAnyRole(['finance', 'it']) || this.hasStaffId([26, 418, 424])) {
      financeMenuItems.push({
        label: 'TRIP LIABILITY',
        routerLink: 'finance/trip-liability-report'
      });
    }

    // NetSuite Import - restricted to finance or IT roles
    // if (this.auth.hasAnyRole(['finance', 'it'])) {
    //   financeMenuItems.push({
    //     label: 'NETSUITE IMPORT'
    //   });
    // }

    // Customer Wallets - restricted to finance, IT, salesAdmin, head, or staff ID 424
    if (this.auth.hasAnyRole(['finance', 'it', 'salesAdmin', 'head']) || this.hasStaffId([424])) {
      financeMenuItems.push({
        label: 'CUSTOMER WALLETS',
        routerLink: 'finance/customer-wallets'
      });
    }

    // On Time Payments - restricted to finance, IT, or staff ID 26
    if (this.auth.hasAnyRole(['finance', 'it']) || this.hasStaffId([26])) {
      financeMenuItems.push({
        label: 'ON TIME PAYMENTS',
        routerLink: 'executive-reporting/on-time-payments'
      });
    }

    // Donations - restricted to finance, IT, or specific staff IDs
    // if (this.auth.hasAnyRole(['finance', 'it']) || this.hasStaffId([6, 773, 26])) {
    //   financeMenuItems.push({
    //     label: 'DONATIONS'
    //   });
    // }

    // financeMenuItems.push(
    //   {
    //     label: 'MILEAGE REIMBURSEMENT'
    //   },
    // )

    financeMenuItems.push({
      label: 'STATE TAX EXEMPTIONS',
      url: 'https://docs.google.com/spreadsheets/d/1j4xhA9RxA7hTL4fgV33Oqdm7xc8V4_Pw__Pquppu9kw/edit#gid=0'
    },)

    financeMenuItems.push({
      label: 'TRIP CHECKLISTS',
      routerLink: 'finance/trip-finance-checklist'
    },)


    //combined the old invoicing with the checklist
    financeMenuItems.push(
      {
        label: 'INVOICING',
        routerLink: 'finance/trip-finance-checklist?invoice=true'
      },
    )

    // Past Due Letters - restricted to finance role only
    // if (this.auth.hasRole('finance')) {
    //   financeMenuItems.push({
    //     label: 'PAST DUE LETTERS'
    //   });
    // }

    // // Public Alert - restricted to finance role or staff ID 26
    // if (this.auth.hasRole('finance') || this.hasStaffId([26])) {
    //   financeMenuItems.push({
    //     label: 'PUBLIC ALERT'
    //   });
    // }

    // financeMenuItems.push(
    //   {
    //     label: 'CASH REQUEST',
    //     items: [
    //       {
    //         label: 'CHECK/ACH'
    //       },
    //       {
    //         label: 'WIRE'
    //       },
    //       {
    //         label: 'WESTERN UNION'
    //       },
    //       {
    //         label: 'ACH VENDORS'
    //       }
    //     ]
    //   },
    // )

    financeMenuItems.push({
      label: 'TRIP PAYMENT TRANSFER',
      url: 'https://docs.google.com/forms/d/e/1FAIpQLSchHfgMaThqlha44oZs_ojYkg1cpGK1jP564qbuC_r-GIh1-w/viewform'
    },)

    // financeMenuItems.push({
    //   label: 'ABACUS',
    //   items: [
    //     {
    //       label: 'TRAINING'
    //     },
    //     {
    //       label: 'TEAMS AND ACCOUNTS'
    //     }
    //   ]
    // })

    financeMenuItems.push({
      label: 'CERTIFICATE OF INSURANCE',
      url: 'https://docs.google.com/forms/d/e/1FAIpQLSesXu7Q0ob0mKin-E5dPbthBA6UKSSKroYmx73MpQ346Cfw4Q/viewform?usp=sf_link'
    })



    departmentsMenuItems.push(
      {
        label: 'FINANCE',
        items: financeMenuItems
      }
    );

    // Disaster Response section
    const disasterMenuItems: MenuItem[] = [
      {
        label: 'RESPONSES',
        routerLink: 'disaster-response'
      },

    ];



    departmentsMenuItems.push({
      label: 'DISASTER RESPONSE',
      items: disasterMenuItems
    });


    const fundraisingMenuItems: MenuItem[] = [
      {
        label: 'DONOR SEARCH',
        routerLink: 'fundraising/donor-search'
      },

    ]

    if (this.auth.hasAnyRole(['it', 'fundraising/*'])) {
      fundraisingMenuItems.push({
        label: 'DONATION IMPORT ERROR REVIEW',
        routerLink: 'fundraising/donation-import-review'
      })
    }


    departmentsMenuItems.push({
      label: 'FUNDRAISING',
      items: fundraisingMenuItems
    })

    // Add DEPARTMENTS nav item with pre-built menu items
    navItems.push({
      label: 'DEPARTMENTS',
      icon: this.icons.solid.faPuzzle,
      hasSubItems: true,
      menuItems: departmentsMenuItems
    });

    navItems.push({
      label: 'ROOTS',
      icon: this.icons.solid.faTreePalm,
      hasSubItems: false,
      url: `${this.configService.mcHost}/missioncontrol/roots`
    })

    navItems.push(

      {
        label: 'MISC',
        icon: this.icons.solid.faScrewdriverWrench,
        hasSubItems: true,
        menuItems: [
          {
            label: 'RESOURCES',
            items: [
              {
                label: 'SEASONAL STAFF SITE',
                url: `${this.configService.mcHost}/missioncontrol/upgrade/seasonal`
              },
              {
                label: 'PELICAN GUIDE',
                url: 'https://app.ppm.org/'
              },
              {
                label: 'LEADERSHIP HANDBOOK',
                url: 'https://leader-app.ppm.org/'
              },
              {
                label: 'PELICAN UNIVERSITY',
                url: 'https://pelicanuniversity.litmos.com/'
              }
            ]
          },
          {
            label: 'TOOLS',
            items: this.getToolsMenuItems()
          }
        ]
      }
    )

    // Cache the result for future use
    this.navItemsCache = navItems;
    return navItems;
  }

  getThemeItem(): ThemeItem {
    const availableThemes = this.themeService.getAvailableThemes();
    const availableFonts = this.themeService.availableFonts;

    return {
      label: 'THEME',
      icon: this.icons.regular.faPalette,
      fonts: availableFonts,
      palettes: availableThemes.map(theme => ({
        name: theme.name,
        displayName: theme.displayName,
        paletteFunction: () => this.themeService.setTheme(theme.name)
      }))
    }
  }


  accountMenuItems = computed(() => {
    const user = this.loggedInUser()

    let returnObj: MenuItem[] = []


    let emergencyContactLink = {
      routerLink: undefined as string | undefined,
      url: undefined as string | undefined
    }

    let emergencyContactLabel = 'Manage Emergency Contacts'

    if (user) {

      const staff = this.staffService.getStaff(user.newstaffId)
      if (staff) {

        if (staff.bambooId) {
          emergencyContactLink = { url: 'https://prayingpelicanmissions.bamboohr.com/employees/contacts.php?id=' + staff.bambooId + '&e=' + staff.bambooId, routerLink: undefined }
          emergencyContactLabel = 'Manage Emergency Contacts (Bamboo HR)'
        }
        else {
          emergencyContactLink = { routerLink: '/staff/profile/' + staff.id, url: undefined }
        }

      }

      returnObj = [
        {
          label: 'My Activities',
          routerLink: '/mission-control/my-activities'
        },
        {
          label: emergencyContactLabel,
          ...emergencyContactLink
        },
        {
          label: 'PTO Request - Bamboo',
          url: 'https://prayingpelicanmissions.bamboohr.com/app/time_off/requests/create'
        },
        {
          label: 'Signature Creator',
          routerLink: '/staff/signature-creator'
        },
        {
          label: 'Accessibility',
          command: () => {
            this.showAccessibilityDialog.set(true)
          }
        },
        {
          label: 'Sign Out',
          command: (e) => {
            this.auth.logout();
          }
        }
      ]

    }

    return returnObj

  })

  // getAccountMenuItems(): MenuItem[] {
  //   return [
  //     {
  //       label: 'Update Profile'
  //     },
  //     {
  //       label: 'PTO Request - Bamboo'
  //     },
  //     {
  //       label: 'Appointment Calendar'
  //     },
  //     {
  //       label: 'Accessibility',
  //       command: () => {
  //         this.showAccessibilityDialog.set(true)
  //       }
  //     },
  //     {
  //       label: 'Sign Out',
  //       command: (e) => {
  //         this.auth.logout();
  //       }
  //     }
  //   ]
  // }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  enableAccessibility() {
    this.mcSettingsService.saveSetting("userwayEnabled", "true").subscribe(() => { })
    this.userwayService.embedUserwayScript()
    this.showAccessibilityDialog.set(false)
    this.accessibilityEnabled.set(true)
  }

  disableAccessibility() {
    this.mcSettingsService.saveSetting("userwayEnabled", "false").subscribe(() => {
      window.location.reload()
    })

  }
}
